import type { Bookmaker } from '$utils/bookmakers';
import type { bookmakerOverroundStore } from '$utils/race';
import type { Writable } from 'svelte/store';

export type RaceStatus =
  | 'Open'
  | 'Closed'
  | 'Interim'
  | 'Abandoned'
  | 'Resulted';

export type Race = {
  id: string;
  name: string;
  number: number;
  status: RaceStatus;
  distance: string;
  betfairMapping: {
    win: string;
    place: string;
  };
  meeting: {
    id: string;
    track: string;
    location: string;
    type: 'Thoroughbred' | 'Harness' | 'Greyhound';
    date: string;
  };
  startTime: Date;
  actualStartTime: Date | null;
  results: number[][] | null;

  runners: {
    id: string;
    number: number;
    barrier: number | null;
    name: string;
    trainerName: string;
    riderName: string;
    scratchedTime: Date | null;
    bookmakerMarkets?: BookmakerMarket[];
    betfairMarkets?: BetfairMarket[];
  }[];

  links: {
    bookmaker: Bookmaker;
    navLink: string;
    affiliateLink: string;
    lastSuccessfulPriceUpdate: Date | null;
  }[];
};

export const MarketTypes = {
  fixedWin: 'fixedWin',
  fixedPlace: 'fixedPlace'
} as const;

export type MarketType = (typeof MarketTypes)[keyof typeof MarketTypes];

export type BookmakerMarket = {
  id: string;
  bookmaker: string;
  fixedWin: {
    price: number;
    lastUpdated: Date;
    openingPrice?: number | null;
    previousPrice?: number;
    flucs?: Fluc[] | null;
  };
  fixedPlace: {
    price: number;
    lastUpdated: Date;
    openingPrice?: number | null;
    previousPrice?: number;
    flucs?:
      | {
          price: number;
          lastUpdated: Date;
        }[]
      | null;
  };
};

export type Fluc = {
  price: number;
  lastUpdated: Date;
};

export type BetfairMarket = {
  id: string;
  sp: number | null;
  marketName: string;
  totalMatched: number;
  marketTotalMatched: number;
  lastPriceTraded: number;
  back: {
    price: number;
    size: number;
    lastUpdated: Date;
  }[];
  lay: {
    price: number;
    size: number;
    lastUpdated: Date;
  }[];
  backFlucs?: Fluc[];
  layFlucs?: Fluc[];
};

export type TopPrices = {
  fixedWin: Map<string, Writable<number[]>>;
  fixedPlace: Map<string, Writable<number[]>>;
}; // runnerId -> [price, price, price]

export type RunnerTopPricesMap = Map<string, RunnerTopPrices>;
export type RunnerTopPrices = {
  fixedWin: Writable<BookmakerMarket[]>;
  fixedPlace: Writable<BookmakerMarket[]>;
};

export type BestMarkets = {
  fixedWin: Map<string, Writable<BookmakerMarket>>;
  fixedPlace: Map<string, Writable<BookmakerMarket>>;
}; // runnerId -> BookmakerMarket

export type Runner = Race['runners'][0];

export type UserEventData = {
  eventId: string;
  userId: string;
  data: {
    columnName: string;
    selectionData: {
      selectionId: string;
      value: string;
    }[];
  }[];
};

export type PriceUpdate = {
  id: string;
  bookmaker: string;
  fixedWin: {
    price: number;

    lastUpdated: Date;
  };
  fixedPlace: {
    price: number;

    lastUpdated: Date;
  };
};

export type EventAlert = {
  id: string;
  eventId: string;
  trigger: 'EVENT_ADDED' | 'PRICES_ADDED' | 'PRICE_ALERT' | 'TIME_TO_JUMP';
  value: string;
};

export type EventAlertInput = {
  trigger: EventAlert['trigger'];
  value: string;
};

export type ActiveEventAlerts = {
  eventId: string;
  meeting?: {
    track?: string;
    type?: string;
    location?: string;
  };
  alerts: {
    trigger: EventAlert['trigger'];
    value: string;
  }[];
};

export type RacesUpdate = {
  id: string;
  status: RaceStatus;
  startTime: Date;
  results: number[][] | null;
};

export type User = {
  id: string;
  name: string;
  email: string;
  billingInformation: {
    subscriptions: {
      status: string;
      trialEnd: Date | null;
      priceId: string;
    }[];
  };
  alertConfig: {
    telegramId?: string | null;
    alertSound?: boolean | null;
    email?: boolean | null;
    discordWebhookUrl?: string | null;
  };
  apiKeys:
    | {
        key: string;
        name: string;
        lastUsed: Date;
        type: 'FULL' | 'RACING' | 'RACING_LIVE';
      }[]
    | undefined;
  siteConfig: {
    hideAbandonedRaces?: boolean | null;
    alternateColourMode?: boolean | null;
    autoEventNav?: boolean | null;
    relativeEventTime?: boolean | null;
    betfairReductionFactor?: number | null;
    prefill?: {
      enabled: boolean | null;
      quickFill: boolean | null;
      defaults: {
        thoroughbred: {
          type: 'TO_WIN' | 'STAKE';
          value: number;
        };
        harness: {
          type: 'TO_WIN' | 'STAKE';
          value: number;
        };
        greyhound: {
          type: 'TO_WIN' | 'STAKE';
          value: number;
        };
      };
      // mbl?: {
      //   thoroughbred?: boolean | null;
      //   harness?: boolean | null;
      //   greyhound?: boolean | null;
      // };
    };
  };
  layoutConfigs: {
    name: string;
    scratchedRunnerVisibility: 'BOTTOM' | 'HIDE' | 'SHOW' | null;
    showRemainingBookmakers: LayoutPreferenceType | null;
    remainingBookmakerSort: 'ALPHABETICAL' | 'MARKET_FRAME' | null;
    runnerSort:
      | 'BARRIER'
      | 'PRICE_ASC'
      | 'PRICE_DESC'
      | null
      | 'H_BARRIER'
      | 'H_BARRIER_DESC';
    runnerPriceSortBookmaker: Bookmaker | null;
    boldTopPrice: boolean | null;
    layoutPreferences: {
      bookmaker?: string | null;
      level?: 'ONE' | 'TWO' | 'WEIGHTED_AVERAGE' | 'ZERO';
      locked?: boolean | null;
      side?: 'BACK' | 'LAY' | null;
      type: LayoutPreferenceType;
      name?: string | null;
      size?: 'SMALL' | 'MEDIUM' | 'LARGE' | null;
      period?:
        | 'ALL'
        | 'NINE_AM'
        | 'SIX_AM'
        | 'ONE_MINUTE'
        | 'FIVE_MINUTES'
        | 'TEN_MINUTES'
        | 'THIRTY_MINUTES'
        | 'ONE_HOUR'
        | 'TWELEVE_HOURS'
        | null;
    }[];
  }[];
};

export type PriceAlert = {
  id: string;
  userId: string;
  eventId: string;
  runnerId: string;
  bookmakers: string[];
  priceIds: string[];
  alertType: 'GREATER_THAN_OR_LESS_THAN' | 'GREATER_THAN' | 'LESS_THAN';
  priceType: 'FIXED_WIN' | 'FIXED_PLACE';
  value: number;
  variance: number;
  persistent: boolean;
  lastTriggeredPrice: {
    priceId: string;
    price: number;
  }[];
  createdAt: Date;
  updatedAt: Date;
};

export type PriceAlertInput = {
  runner?: Runner;

  eventId: PriceAlert['eventId'];
  runnerId: PriceAlert['runnerId'];
  priceIds: PriceAlert['priceIds'];
  alertType: PriceAlert['alertType'];
  priceType: PriceAlert['priceType'];
  bookmakers: PriceAlert['bookmakers'];
  value: PriceAlert['value'];
  variance?: PriceAlert['variance'];
  persistent?: PriceAlert['persistent'];
  userId?: PriceAlert['userId'];
};

export type LayoutConfig = User['layoutConfigs'][0];
export type LayoutPreference = LayoutConfig['layoutPreferences'][0];
export type SiteConfig = User['siteConfig'];
export type AlertConfig = User['alertConfig'];

export const enum LayoutPreferenceType {
  'BEST_PRICE_PLACE' = 'BEST_PRICE_PLACE',
  'BEST_PRICE_WIN' = 'BEST_PRICE_WIN',
  'FIXED_PLACE' = 'FIXED_PLACE',
  'FIXED_WIN' = 'FIXED_WIN',
  'FIXED_PLACE_FLUCS' = 'FIXED_PLACE_FLUCS',
  'FIXED_WIN_FLUCS' = 'FIXED_WIN_FLUCS',
  'NONE' = 'NONE',
  'SPACER' = 'SPACER',
  'TEXT' = 'TEXT'
}

export type GQLError = {
  message: string;
  locations: {
    line: number;
    column: number;
  }[];
  path?: string[];
  extensions?: {
    code: string;
    exception?: {
      stacktrace: string[];
    };
  };
};

type GQLResponse<T> =
  | {
      data: T | undefined;
      errors: GQLError[] | undefined;
    }
  | undefined;

export type BookmakerMarketResponse = GQLResponse<{
  bookmakerMarket: BookmakerMarket;
}>;

export type RaceResponse = GQLResponse<{
  race: Race;
}>;

export type UserEventDataResponse = GQLResponse<{
  userEventData: UserEventData;
}>;

export type RacesResponse = GQLResponse<{
  races: Race[];
}>;

export type UserResponse = GQLResponse<{
  user: User;
}>;

export type PriceAlertsResponse = GQLResponse<{
  priceAlerts: PriceAlert[];
}>;

export type AllEventAlertsResponse = GQLResponse<{
  allEventAlerts: ActiveEventAlerts[];
}>;

export type UpdateEventAlertsResponse = GQLResponse<{
  updateAlerts: ActiveEventAlerts;
}>;

export type AddPriceAlertResponse = GQLResponse<{
  addPriceAlert: PriceAlert;
}>;

export type RemovePriceAlertResponse = GQLResponse<{
  removePriceAlert: PriceAlert;
}>;

export type RacePriceUpdateResponse = GQLResponse<{
  priceUpdates: PriceUpdate[];
}>;

export type RaceBetfairUpdateResponse = GQLResponse<{
  betfairUpdates: BetfairMarket[];
}>;

export type RaceUpdateResponse = GQLResponse<{
  racesUpdates: RacesUpdate;
}>;

export type EventAlertsResponse = GQLResponse<{
  eventAlerts: EventAlert;
}>;

export type MarketOverrounds = Map<
  Bookmaker | string,
  {
    fixedWin: ReturnType<typeof bookmakerOverroundStore>;
    fixedPlace: ReturnType<typeof bookmakerOverroundStore>;
  }
>;
